import { Client, IMessage } from '@stomp/stompjs';

import storage, { EStorageKey } from '@aqx-core/common/storage';

import { formatTopicId, formatTopicPath } from '../utils';
import { ISocketTopic } from './types';

export enum EQuoteMessageType {
  LIVE = 'LIVE'
}

export interface IQuoteTopicMessageData {
  S: string; // Symbol
  H: number; // High
  O: number; // Open
  V: number; // Volume
  L: number; // Low
  PC: number; // Previous Close
  NUM: number; // Number of Trades
  D: number; // Delta
  G: number; // Gamma
  VG: number; // Vega
  TH: number; // Theta
  T: number; // Timestamp
}

export interface IQuoteTopicMessage {
  M: EQuoteMessageType;
  D: IQuoteTopicMessageData;
}

export interface IQuoteTopicReceiver {
  subscriptionKey: string;
  payload: (payload: IQuoteTopicMessage | undefined, symbol?: string) => void;
}

interface ISubscribeParams {
  symbol: string;
  receiver: IQuoteTopicReceiver;
}

interface IProps {
  client: Client;
}

class QuoteTopic implements ISocketTopic {
  private client: Client;
  private receivers: Record<string, Set<IQuoteTopicReceiver> | undefined> = {};

  constructor(props: IProps) {
    const { client } = props;

    this.client = client;
  }

  public subscribe = (params: ISubscribeParams) => {
    const { symbol, receiver } = params;
    const { subPath, topicId } = this.mapTopicIds(symbol);

    if (!this.client) {
      return;
    }

    if (this.receivers[subPath]) {
      let isKeyExisting = false;
      this.receivers[subPath]?.forEach((r) => {
        isKeyExisting = r.subscriptionKey === receiver.subscriptionKey;
      });

      if (!isKeyExisting) {
        this.receivers[subPath]?.add(receiver);
      }
    } else {
      this.receivers[subPath] = new Set([receiver]);
      try {
        this.client.subscribe(formatTopicPath(subPath), (message) => this.onReceive(subPath, message, symbol), {
          id: topicId
        });
      } catch (err) {}
    }
  };

  public unsubscribe = (params: ISubscribeParams) => {
    const { symbol, receiver } = params;
    const { subPath, topicId } = this.mapTopicIds(symbol);

    if (!this.client) {
      return;
    }

    if (this.receivers[subPath]?.size !== 0) {
      let lastItem = null;
      this.receivers[subPath]?.forEach((r) => {
        if (r.subscriptionKey === receiver.subscriptionKey) {
          lastItem = r;
        }
      });

      if (lastItem) {
        this.receivers[subPath]?.delete(lastItem);
      }
    }

    if (this.receivers[subPath]?.size === 0) {
      try {
        this.client?.unsubscribe(topicId);
      } catch (err) {}

      this.receivers[subPath] = undefined;
      delete this.receivers[subPath];
    }
  };

  public clearSubscriptions = () => {
    this.receivers = {};
  };

  public reconnect = () => {
    const { receivers } = this;

    if (receivers) {
      Object.keys(receivers).forEach((subPath) => {
        this.client.subscribe(formatTopicPath(subPath), (message) => this.onReceive(subPath, message), {
          id: formatTopicId(subPath)
        });
      });
    }
  };

  private onReceive = (id: string, message: IMessage, symbol?: string) => {
    const receivers = this.receivers[id];

    if (!receivers || !message) {
      return;
    }

    const payload = JSON.parse(message.body) as IQuoteTopicMessage;

    receivers.forEach((receiver) => receiver.payload(payload, symbol));
  };

  private mapTopicIds = (symbol: string) => {
    const marketSubscription = storage.getItem(EStorageKey.MARKET_SUBSCRIPTION);
    const subPath = `quote/${marketSubscription}/${symbol}`;

    return {
      subPath,
      topicId: formatTopicId(subPath)
    };
  };
}

export default QuoteTopic;
